import { useEffect, useState } from 'react';

interface HeroSectionProps {
  onContactClick: () => void;
}

export default function HeroSection({ onContactClick }: HeroSectionProps) {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setVisible(true), 100);
    return () => clearTimeout(t);
  }, []);

  return (
    <section
      id="hero"
      className="relative min-h-screen flex flex-col overflow-hidden"
      style={{ background: '#F4F7FB' }}
    >
      {/* Main hero body */}
      <div className="flex flex-1 min-h-screen">

        {/* ── Left Panel: Text Content ── */}
        <div className="relative z-10 flex flex-col justify-center w-full lg:w-[50%] px-10 xl:px-20 pt-28 pb-12">

          {/* Category Badge */}
          <div
            className={`inline-flex items-center gap-2 px-4 py-2 rounded-full mb-8 text-sm font-bold border w-fit transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}
            style={{ background: '#E8F4FB', borderColor: '#B8D9F0', color: '#0A508C' }}
          >
            <span className="w-2 h-2 rounded-full inline-block animate-pulse" style={{ background: '#0A508C' }} />
            농산물 패키징 전문 B2B
          </div>

          {/* Headline */}
          <h1
            className={`text-5xl xl:text-6xl 2xl:text-7xl font-black mb-6 tracking-tight transition-all duration-700 delay-100 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}
            style={{ color: '#0A508C', lineHeight: '1.2' }}
          >
            과일 패키지,<br />
            라벨, 부착까지<br />
            <span style={{ color: '#2BC0D0' }}>한 번에.</span>
          </h1>

          {/* Sub Texts */}
          <div className={`mb-10 transition-all duration-700 delay-200 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
            <p className="text-lg font-semibold mb-2 leading-relaxed" style={{ color: '#2D4A5C' }}>
              농산물 판매를 위한 패키징 실무를 신진패키징이 함께합니다
            </p>
            <p className="text-base leading-snug" style={{ color: '#5A7A8A' }}>
              팩 공급 · 맞춤 라벨 디자인 · 라벨 부착 서비스<br />
              받는 즉시 출하 가능한 상태로 납품합니다.
            </p>
          </div>

          {/* CTA Buttons */}
          <div className={`flex flex-wrap gap-3 mb-12 transition-all duration-700 delay-300 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
            <a
              href="https://smartstore.naver.com"
              target="_blank"
              rel="nofollow noreferrer"
              className="inline-flex items-center gap-2 px-8 py-4 rounded-lg text-white font-bold text-base cursor-pointer whitespace-nowrap transition-opacity hover:opacity-90"
              style={{ background: 'linear-gradient(135deg, #0A508C, #3A7CC0)' }}
            >
              <i className="ri-store-2-line text-lg" />
              제품 보러가기
              <i className="ri-arrow-right-line text-base" />
            </a>
            <button
              onClick={onContactClick}
              className="inline-flex items-center gap-2 px-8 py-4 rounded-lg font-bold text-base cursor-pointer whitespace-nowrap border-2 transition-colors hover:bg-[#0A508C]/5"
              style={{ borderColor: '#0A508C', color: '#0A508C', background: 'white' }}
            >
              <i className="ri-chat-3-line text-lg" />
              상담 문의하기
            </button>
          </div>

          {/* Stats Row */}
          <div className={`flex gap-8 pt-8 border-t border-gray-200 transition-all duration-700 delay-400 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
            {[
              { value: '176+', label: '다양한 팩 종류' },
              { value: '3가지', label: '원스톱 서비스' },
              { value: '100%', label: '맞춤 라벨 제작' },
            ].map((stat) => (
              <div key={stat.label}>
                <div className="text-2xl font-black mb-0.5" style={{ color: '#0A508C' }}>{stat.value}</div>
                <div className="text-sm" style={{ color: '#7A9BAD' }}>{stat.label}</div>
              </div>
            ))}
          </div>
        </div>

        {/* ── Right Panel: Full-height Image ── */}
        <div
          className={`hidden lg:flex flex-col w-[50%] relative transition-all duration-700 delay-200 ${visible ? 'opacity-100' : 'opacity-0'}`}
          style={{ background: 'linear-gradient(160deg, #E0EDF7 0%, #D0E4F4 100%)' }}
        >
          {/* Full-height image */}
          <div className="absolute inset-0">
            <img
              src="https://static.readdy.ai/image/8c1d21b01113fd26c1c387000dbd0877/45d7d6b3562cc06e9fccac44e78ece50.png"
              alt="신진패키징 농산물 패키징 원스톱 서비스"
              className="w-full h-full object-cover object-top"
            />
            {/* subtle overlay for card readability */}
            <div className="absolute inset-0" style={{ background: 'linear-gradient(to bottom, rgba(10,80,140,0.08) 0%, rgba(10,80,140,0.18) 100%)' }} />
          </div>

        </div>
      </div>
    </section>
  );
}
